import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useSearchParams } from "react-router-dom";

import { FormCard } from "../components/ui";
import PublicPageFrame from "../components/ui/PublicPageFrame";
import { sharedApi } from "../services/sharedApi";

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation("auth");
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError(t("resetPassword.errors.tokenMissing"));
      return;
    }

    if (!newPassword) {
      setError(t("resetPassword.errors.passwordRequired"));
      return;
    }

    if (newPassword.length < 6) {
      setError(t("resetPassword.errors.passwordTooShort"));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t("resetPassword.errors.passwordMismatch"));
      return;
    }

    setLoading(true);
    try {
      await sharedApi.auth.resetPassword({ token, password: newPassword });
      setSuccess(true);
    } catch {
      setError(t("resetPassword.errors.resetFailed"));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <PublicPageFrame>
        <FormCard title={t("resetPassword.title")} maxWidth="sm">
          <Alert severity="error" sx={{ mb: 2 }}>
            {t("resetPassword.invalidToken")}
          </Alert>
          <Button
            component={RouterLink}
            to="/forgot-password"
            variant="contained"
            fullWidth
          >
            {t("resetPassword.requestNewLink")}
          </Button>
        </FormCard>
      </PublicPageFrame>
    );
  }

  return (
    <PublicPageFrame>
      <FormCard
        title={t("resetPassword.title")}
        subtitle={success ? undefined : t("resetPassword.description")}
        maxWidth="sm"
      >
        {success ? (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              {t("resetPassword.successMessage")}
            </Alert>
            <Typography variant="h6" gutterBottom>
              {t("resetPassword.successTitle")}
            </Typography>
            <Button
              component={RouterLink}
              to="/auth"
              variant="contained"
              fullWidth
              sx={{ mt: 2 }}
            >
              {t("resetPassword.goToLogin")}
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <Collapse in={!!error}>
              <Alert
                severity="error"
                onClose={() => setError(null)}
                sx={{ mb: 2 }}
              >
                {error}
              </Alert>
            </Collapse>

            <TextField
              fullWidth
              label={t("resetPassword.newPassword")}
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={loading}
              required
              helperText={t("resetPassword.passwordHelper")}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label={t("resetPassword.confirmPassword")}
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              required
              sx={{ mb: 3 }}
            />

            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={loading || !newPassword || !confirmPassword}
              startIcon={loading ? <CircularProgress size={20} /> : null}
              sx={{ mb: 2 }}
            >
              {loading
                ? t("resetPassword.resetting")
                : t("resetPassword.resetButton")}
            </Button>

            <Button component={RouterLink} to="/auth" fullWidth variant="text">
              {t("forgotPassword.backToLogin")}
            </Button>
          </Box>
        )}
      </FormCard>
    </PublicPageFrame>
  );
};

export default ResetPasswordPage;
